// LSP-17342 (github-actions review 5360786614, CRITICAL): the ONE reason an Auth0 handoff can be
// refused rather than fail — a bfcache-restore recovery already owns this page's navigation. It
// has to be distinguishable from a genuine loginWithRedirect failure (network down, Auth0 outage)
// at every catch site, because the correct responses are opposites: a suppressed redirect means
// "do nothing, the page is already leaving", while a genuine failure means the caller's work did
// NOT happen and continuing would run on stale state. Callers used to see one indistinguishable
// Error and treat both as benign, which silently completed an authenticated boot holding an
// access token still scoped to the PREVIOUS organization.
//
// Lives here rather than in auth.js on purpose: src/auth is module-mocked by the global test setup
// and by individual specs, so a helper exported from there is `undefined` in any test that mocks
// it — and a catch site calling an undefined guard throws from inside its own catch.
export default class Auth0RedirectSuppressedError extends Error {
	// readonly: these are correctness-load-bearing markers — the router branches on
	// auth0RedirectSuppressed to decide whether boot may continue, so nothing should be able to
	// mutate it after construction.
	readonly callerContext: string | null;
	readonly auth0RedirectSuppressed: boolean;

	constructor(callerContext?: string | null) {
		super("Auth0 navigation was suppressed because a bfcache-restore recovery already owns this page's navigation");
		this.name = "Auth0RedirectSuppressedError";
		this.auth0RedirectSuppressed = true;
		this.callerContext = callerContext ?? null;
	}
}

// Duck-typed rather than `instanceof`: the error crosses an `await` and a Vue component boundary,
// and a duplicated module instance (test isolation, a future chunk split) would silently break
// `instanceof` and send a suppressed redirect down the genuine-failure path — re-authenticating a
// user whose page is already navigating away.
//
// The MARKER PROPERTY is required, not merely one of two accepted signals (review 5023486490 #5).
// Accepting `name` alone meant any genuine failure that happened to carry that name was classified
// as suppression, and the suppression branch is the one that does NOT re-validate the organization
// — so a false positive there resumes boot on a stale-org token, which is precisely the CRITICAL
// this file was introduced to fix. The marker is now the ONLY signal — `name` is not consulted at
// all, because a name that agrees with a missing marker adds nothing and a name that disagrees with
// a present marker should still be trusted (the constructor sets both). Message text is never
// consulted either.
export function isAuth0RedirectSuppressedError(error: unknown): error is Auth0RedirectSuppressedError {
	return (error as { auth0RedirectSuppressed?: unknown } | null | undefined)?.auth0RedirectSuppressed === true;
}
